import { useRef, useState, useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { ApplicantsDatabase } from "../../templates/ApplicantsDatabase";
import { fetchApplicantsRequest } from "../../../redux/applicant/actions";
import { fetchPositions } from "../../../redux/position/action";
import { CSVLink } from "react-csv";
import moment from "moment";
import { format } from "../../../constants";
import { sortSkills } from "../../../utils/helpers/transform";

const ApplicantsDatabasePage = () => {
  const [formik, setFormik] = useState<any>();
  const csvRef = useRef<any>();
  const [csvValues, setCsvValues] = useState<Array<any>>([]);
  const [selectedPhases, setSelectedPhases] = useState<any>([]);
  const [selectedPositions, setSelectedPositions] = useState<any>([]);

  const isLoading = useSelector(
    (state: RootStateOrAny) => state.applicant.isLoading
  );
  const applicants = useSelector(
    (state: RootStateOrAny) => state.applicant.applicants
  );
  const isLoadingPosition = useSelector(
    (state: RootStateOrAny) => state.position.isLoading,
  );
  const positions = useSelector(
    (state: RootStateOrAny) => state.position.positions
  );

  const formikRef = (node: any) => {
    if (node !== null) {
      setFormik(node.values);
    }
  };

  const dispatch = useDispatch();

  const [options, setOptions] = useState<
    Array<{ title: string; selected: boolean }>
  >([
    { title: "Name", selected: false },
    { title: "LinkedIn", selected: false },
    { title: "Contact Number", selected: false },
    { title: "Email", selected: false },
    { title: "Best time to contact", selected: false },
    { title: "Resume", selected: false },
    { title: "Date Applied", selected: false },
    { title: "Source of Application", selected: false },
    { title: "Expected Salary", selected: false },
    { title: "Knowledge & Skills", selected: false },
    { title: "Recruitment Status", selected: false },
  ]);
  const [optionCounter, setOptionCounter] = useState<number>(0);
  const [disableDownload, setDisableDownload] = useState<boolean>(false);

  useEffect(() => {
    if (
      formik &&
      formik.start_date !== null &&
      formik.end_date !== null &&
      selectedPhases.length > 0 &&
      selectedPositions.length > 0
    ) {
      let allPosIndex = selectedPositions.indexOf("All");
      let allPhaseIndex = selectedPhases.indexOf("All");
      let tempPos = selectedPositions;
      let tempPhase = selectedPhases;

      allPosIndex !== -1 && tempPos.splice(allPosIndex, 1);
      allPhaseIndex !== -1 && tempPhase.splice(allPhaseIndex, 1);

      dispatch(
        fetchApplicantsRequest("database", undefined, [
          ["positionApplied", "==", tempPos],
          ["applicationStatus.currentStatus", "in", tempPhase],
          ["createdAt", ">", moment(formik.start_date).startOf("day").toDate()],
          ["createdAt", "<", formik.end_date.toDate()],
        ])
      );
    }
  }, [formik, selectedPositions, selectedPhases]);

  useEffect(() => {
    dispatch(fetchPositions(undefined, [["status", "==", "Open"]]));
  }, [dispatch]);

  const onDownloadCsv = () => {
    csvRef.current?.link.click();
  };

  const setHeaders = (selected: { title: string; selected: boolean }[]) => {
    return selected
      .filter((option) => option.selected)
      .map((value) => {
        let key = "";
        switch (value.title) {
          case "Name":
            key = "name";
            break;
          case "LinkedIn":
            key = "linkedinUrl";
            break;
          case "Contact Number":
            key = "contactNumber";
            break;
          case "Email":
            key = "email";
            break;
          case "Best time to contact":
            key = "timeToContact";
            break;
          case "Date Applied":
            key = "createdAt";
            break;
          case "Resume":
            key = "resume";
            break;
          case "Source of Application":
            key = "platform";
            break;
          case "Expected Salary":
            key = "salary";
            break;
          case "Knowledge & Skills":
            key = "skills";
            break;
          case "Recruitment Status":
            key = "recruitmentStatus";
            break;
        }

        return {
          key,
          label: value.title,
        };
      });
  };

  const setSkills = (skills: Object) => {
    let skillsLists = "";
    sortSkills(skills).forEach((item) => {
      skillsLists += `• ${item.skill}: ${item.value} \n`;
    });
    return skillsLists;
  };

  useEffect(() => {
    let selectedOptions = setHeaders(options);
    selectedOptions = [
      ...selectedOptions,
      { key: "positionApplied", label: "Position" },
      { key: "recruitmentStatus", label: "Phase" },
    ];
    const csvData = applicants.map((applicant: any, key: number) => {
      let applicantInfo: any[] = [];
      const keys = Object.keys(applicant);
      selectedOptions.forEach((option) => {
        const selected = keys.find((key) => option.key === key);
        if (selected && option.key !== "skills") {
          applicantInfo = [...applicantInfo, applicant[option.key] || "N/A"];
        }
        if (selected && option.key === "skills") {
          applicantInfo = [
            ...applicantInfo,
            setSkills(applicant.skills),
          ];
        }
      });
      return applicantInfo;
    });

    let tableHeaders = [...headers.map((header) => header.label), "Position", "Phase"];
    setCsvValues([
      [
        `Start Date: ${moment(formik?.start_date).format(format.dateFormat)}`,
        `End Date:  ${moment(formik?.end_date).format(format.dateFormat)}`,
      ],
      tableHeaders,
      ...csvData,
    ]);
  }, [applicants, options]);

  const toggleSelected = (title: string) => {
    let dupOptions = [...options];
    let objIndex = dupOptions.findIndex((obj) => obj.title === title);
    dupOptions[objIndex].selected = !dupOptions[objIndex].selected;
    dupOptions[objIndex].selected === true
      ? setOptionCounter(optionCounter + 1)
      : setOptionCounter(
          optionCounter === 0 ? optionCounter : optionCounter - 1
        );
    setOptions(dupOptions);
  };

  const buttonSelector = (select: boolean) => {
    let newOptions = [...options];
    newOptions.map((option) => {
      return (option.selected = select);
    });
    setOptionCounter(select ? options.length : 0);
    setOptions(newOptions);
  };

  const phasesList = [
    "New Applicant",
    "Shortlisted",
    "Technical Exam",
    "Initial Interview",
    "Psychological Exam",
    "Final Interview",
    "Failed",
  ];
  const phases = phasesList.map((pos) => {
    return { label: pos, value: pos };
  });


  const headers = setHeaders(options);

  useEffect(() => {
    setDisableDownload(!optionCounter);
  }, [options, optionCounter]);

  return (
    <>
      <ApplicantsDatabase
        formRef={formikRef}
        options={options}
        optionCounter={optionCounter}
        toggleSelected={toggleSelected}
        buttonSelector={buttonSelector}
        onDownloadCsv={onDownloadCsv}
        phases={phases}
        positions={positions.map((pos: { position: string }) => {
          return { label: pos.position, value: pos.position };
        })}
        isLoading={isLoading || isLoadingPosition}
        applicants={applicants}
        setSelectedPhases={setSelectedPhases}
        selectedPhases={selectedPhases}
        setSelectedPositions={setSelectedPositions}
        selectedPositions={selectedPositions}
        disableDownload={disableDownload}
      />
      <CSVLink
        style={{ visibility: "hidden" }}
        data={csvValues}
        filename={`Applicants Database (${formik?.start_date?.format(
          "YYYY-MM-DD"
        )} - ${formik?.end_date?.format("YYYY-MM-DD")}).csv`}
        ref={csvRef}
      />
    </>
  );
};

export default ApplicantsDatabasePage;
